import type { ProtoWallet } from '@ducat-unit/client-sdk';
import { AddressType, getAddressInfo } from 'bitcoin-address-validation';
import { v4 as uuidv4 } from 'uuid';

import type { DucatClientAPI } from '@/context/DucatClientCtx';
import { getSnapWalletAccount } from '@/lib/snap-wallet-reader';

export type QuantaWalletProvider = 'xverse' | 'unisat' | 'metamask';
export type QuantaWalletRole = 'payment' | 'vault';
export interface QuantaWalletIdentity {
  address: string;
  publicKey: string;
}
export interface QuantaWalletIdentities {
  payment: QuantaWalletIdentity;
  vault: QuantaWalletIdentity;
}
export interface QuantaSignInIdentities {
  payment: QuantaWalletIdentity;
  vault: QuantaWalletIdentity | null;
}

function taprootPublicKey(publicKey: string): string | null {
  if (/^[a-f0-9]{64}$/i.test(publicKey)) return publicKey.toLowerCase();
  if (/^0[23][a-f0-9]{64}$/i.test(publicKey)) return publicKey.slice(2).toLowerCase();
  return null;
}

function canonicalAddress(address: string): string {
  return /^(bc1|tb1|bcrt1)/i.test(address) ? address.toLowerCase() : address;
}

export async function readLiveWalletRoles(
  provider: QuantaWalletProvider,
  wallet?: ProtoWallet,
): Promise<{ payment: QuantaWalletIdentity; vault: QuantaWalletIdentity | null } | null> {
  try {
    if (provider === 'metamask') {
      if (!wallet) return null;
      const account = await getSnapWalletAccount(wallet);
      return {
        payment: { address: account.sats.address, publicKey: account.sats.pubkey },
        vault: account.vault ? { address: account.vault.address, publicKey: account.vault.pubkey } : null,
      };
    }
    if (provider === 'unisat') {
      // biome-ignore lint/suspicious/noExplicitAny: window.unisat is injected at runtime.
      const unisat = (globalThis as any)?.unisat;
      if (!unisat) {
        return null;
      }
      const accounts: string[] = await unisat.getAccounts();
      const address = accounts?.[0];
      const rawPublicKey: string = await unisat.getPublicKey();
      if (!address || !rawPublicKey) {
        return null;
      }
      // UniSat's getPublicKey() returns the compressed 33-byte key, but the app
      // normalizes UniSat taproot keys to x-only (strips the leading byte) in
      // computeAccountRecord_unisat — that x-only key is what was registered and
      // what /check resolves by. Apply the SAME normalization here so the auth
      // header's x-user-public-key matches; otherwise UniSat taproot users look
      // unregistered / fail login.
      const isTaproot = getAddressInfo(address).type === AddressType.p2tr;
      const publicKey = isTaproot ? taprootPublicKey(rawPublicKey) : rawPublicKey;
      if (!publicKey) return null;
      const payment = { address, publicKey };
      return { payment, vault: isTaproot ? payment : null };
    }

    const { AddressPurpose, request } = await import('@sats-connect/core');
    const account = await request('wallet_getAccount', null);
    if (account.status !== 'success') {
      return null;
    }
    const payment = account.result.addresses.find((a) => a.purpose === AddressPurpose.Payment);
    if (!payment?.address || !payment.publicKey) {
      return null;
    }
    const ordinals = account.result.addresses.find((a) => a.purpose === AddressPurpose.Ordinals);
    return {
      payment: { address: payment.address, publicKey: payment.publicKey },
      vault: ordinals?.address && ordinals.publicKey ? { address: ordinals.address, publicKey: ordinals.publicKey } : null,
    };
  } catch (error) {
    if (provider === 'metamask' && error && typeof error === 'object' && 'action' in error && error.action === 'switch-network') {
      throw error;
    }
    return null;
  }
}

/**
 * Resolve both roles from the live provider, then match them to the connected
 * app wallet. In particular, Xverse payment may be nested SegWit while the app
 * derives a native SegWit funds address: compare that role's key, not its address.
 * The vault must match both its actual address/network and taproot key.
 */
export async function getActiveWalletIdentities(
  provider: QuantaWalletProvider = 'xverse',
  wallet?: ProtoWallet,
): Promise<QuantaWalletIdentities | null> {
  const identities = await getQuantaSignInIdentities(provider, wallet);
  return identities?.vault ? { payment: identities.payment, vault: identities.vault } : null;
}

/** Legacy UniSat accounts may sign in with payment only; they cannot prove a derived vault. */
export async function getQuantaSignInIdentities(
  provider: QuantaWalletProvider = 'xverse',
  wallet?: ProtoWallet,
): Promise<QuantaSignInIdentities | null> {
  if (!wallet?.account?.funds) return null;
  const roles = await readLiveWalletRoles(provider, wallet);
  if (!roles) return null;
  try {
    const paymentInfo = getAddressInfo(roles.payment.address);
    const expectedPaymentInfo = getAddressInfo(wallet.account.funds.address);
    const paymentPublicKey =
      paymentInfo.type === AddressType.p2tr ? taprootPublicKey(roles.payment.publicKey) : roles.payment.publicKey.toLowerCase();
    const expectedPaymentKey =
      paymentInfo.type === AddressType.p2tr ? taprootPublicKey(wallet.account.funds.pubkey) : wallet.account.funds.pubkey.toLowerCase();
    if (!paymentPublicKey || paymentPublicKey !== expectedPaymentKey || paymentInfo.network !== expectedPaymentInfo.network) return null;
    if (provider === 'unisat' && paymentInfo.type !== AddressType.p2tr) {
      return { payment: roles.payment, vault: null };
    }
    if (!roles.vault || !wallet.account.vault) return null;
    const vaultInfo = getAddressInfo(roles.vault.address);
    const vaultPublicKey = taprootPublicKey(roles.vault.publicKey);
    const expectedVaultKey = taprootPublicKey(wallet.account.vault.pubkey);
    if (
      vaultInfo.type !== AddressType.p2tr ||
      paymentInfo.network !== vaultInfo.network ||
      !vaultPublicKey ||
      vaultPublicKey !== expectedVaultKey ||
      canonicalAddress(roles.vault.address) !== canonicalAddress(wallet.account.vault.address)
    ) {
      return null;
    }
    return {
      payment: roles.payment,
      vault: { address: canonicalAddress(roles.vault.address), publicKey: vaultPublicKey },
    };
  } catch {
    return null;
  }
}

/** Keep legacy payment signing available for existing migration proofs. */
export async function getActiveWalletIdentity(
  provider: QuantaWalletProvider = 'xverse',
  wallet?: ProtoWallet,
  role: QuantaWalletRole = 'payment',
): Promise<QuantaWalletIdentity | null> {
  if (role === 'vault') return (await getActiveWalletIdentities(provider, wallet))?.vault ?? null;
  return (await readLiveWalletRoles(provider, wallet))?.payment ?? null;
}

export async function sha256Hex(value: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(value);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));

  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

interface QuantaHeaderParams {
  userPublicKey: string;
  address: string;
  hash: string;
  value: string;
  signedMessage: string;
}

export type QuantaHeaders = {
  'x-hash': string;
  'x-value': string;
  'x-user-public-key': string;
  'x-address': string;
  'x-signed-message': string;
  'ngrok-skip-browser-warning'?: boolean;
};

export function buildQuantaHeaders(params: QuantaHeaderParams): QuantaHeaders {
  return {
    'x-hash': params.hash,
    'x-value': params.value,
    'x-user-public-key': params.userPublicKey,
    'x-address': params.address,
    'x-signed-message': params.signedMessage,
    'ngrok-skip-browser-warning': true,
  };
}

export async function signQuantaAuth({
  address,
  pubkey, // remove
  client,
}: {
  address: string;
  pubkey: string;
  client: Pick<DucatClientAPI, 'signMessage'>;
}): Promise<QuantaHeaderParams> {
  const value = uuidv4();
  // const hash = bitcore.crypto.Hash.sha256(Buffer.from(value)).toString('hex');
  const hash = await sha256Hex(value);
  const signedMessageResponse = await client.signMessage({ address, message: hash });

  if (!signedMessageResponse?.result?.signature) {
    throw Error('Failed to sign message');
  }

  const signedMessage = signedMessageResponse?.result?.signature;

  return {
    address,
    hash,
    value,
    signedMessage,
    userPublicKey: pubkey,
  };
}
